import { useEffect } from "react";
import { PROGRAM_SHORT } from "@/lib/api";
import { setSeo } from "@/lib/seo";
// structured-data.js is CommonJS (it is also `require`d by
// scripts/prerender-seo.js), so it is pulled in as a namespace rather than with
// named imports.
import * as structuredData from "@/lib/structured-data";

/**
 * Applies the per-sponsor SEO title/description/structured data.
 *
 * Extracted verbatim from src/pages/SponsorProfile.jsx during the code-quality
 * pass: the title/description strings, noindex rule for paused profiles and the
 * JSON-LD graph are byte-for-byte identical to the previous inline effect.
 */
export default function useSponsorProfileSeo(sponsor, userId) {
  useEffect(() => {
    if (!sponsor) return;
    const su = sponsor.user || {};
    const name = su.display_name || "Peer sponsor";
    const program = PROGRAM_SHORT[sponsor.recovery_program] || "Peer";
    const place = [su.location?.city, su.location?.state].filter(Boolean).join(", ");
    const years = sponsor.years_in_recovery;
    const availability =
      sponsor.availability_status === "accepting"
        ? "Currently accepting new sponsees"
        : sponsor.availability_status === "limited"
        ? "Limited availability for new sponsees"
        : "Not accepting new sponsees right now";

    const title = `${name} - ${program} Recovery Sponsor${place ? ` in ${place}` : ""} | Sponsor Me`;
    const about = (sponsor.about_me || "").replace(/\s+/g, " ").trim();
    const intro = [
      `${name} is a volunteer ${program} peer sponsor`,
      years ? `with ${years} year${years === 1 ? "" : "s"} in recovery` : null,
      place ? `based in ${place}` : null,
    ]
      .filter(Boolean)
      .join(" ");
    const description = `${intro}. ${availability}. ${about}`
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 200)
      .replace(/\s+\S*$/, "");

    setSeo({
      title: title.slice(0, 120),
      description,
      path: `/sponsors/${userId}`,
      index: !sponsor.is_paused,
      ogType: "profile",
      schema: structuredData.sponsorProfileGraph({
        title: title.slice(0, 120),
        description,
        path: `/sponsors/${userId}`,
        index: !sponsor.is_paused,
      }),
    });
  }, [sponsor, userId]);
}
